import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTenant } from '../../contexts/TenantContext';
import { publicApi } from '../../services/publicApi';
import EventStatus, { RegistrationStatus, EventCountdown, TeamRegistrationProgress } from '../../components/public/EventStatus';
import EventCard from '../../components/public/EventCard';
import Breadcrumbs from '../../components/public/Breadcrumbs';

const EventDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { tenant } = useTenant();
  
  const [event, setEvent] = useState(null);
  const [similarEvents, setSimilarEvents] = useState([]);
  const [navigation, setNavigation] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('details');

  useEffect(() => {
    loadEvent();
  }, [id]);

  const loadEvent = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await publicApi.getEvent(id);
      
      if (response.data.event) {
        setEvent(response.data.event);
        setSimilarEvents(response.data.similar_events || []);
        setNavigation(response.data.navigation || {});
        
        // Update page title
        document.title = `${response.data.event.event_name} - ${tenant?.name || 'Baseball Federation'}`;
        
        // Update meta description
        if (response.data.event.meta_description) {
          let metaDescription = document.querySelector('meta[name="description"]');
          if (!metaDescription) {
            metaDescription = document.createElement('meta');
            metaDescription.name = 'description';
            document.head.appendChild(metaDescription);
          }
          metaDescription.content = response.data.event.meta_description;
        }
      }
    } catch (error) {
      console.error('Failed to load event:', error);
      if (error.response?.status === 404) {
        setError('Event not found');
      } else {
        setError('Failed to load event details');
      }
    } finally {
      setLoading(false);
    }
  };

  const breadcrumbs = [
    { title: 'Home', url: '/' },
    { title: 'Events', url: '/events' },
    { title: event?.event_name || 'Event', current: true }
  ];

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const shareEvent = () => {
    if (navigator.share) {
      navigator.share({
        title: event.event_name,
        text: event.event_description,
        url: window.location.href
      });
    } else {
      // Fallback to copying URL to clipboard
      navigator.clipboard.writeText(window.location.href);
      // You could show a toast notification here
    }
  };

  if (loading) {
    return (
      <div className="event-detail">
        <Breadcrumbs items={breadcrumbs} />
        <div className="container">
          <div className="event-loading">
            <div className="loading-spinner">
              <i className="fas fa-spinner fa-spin"></i>
            </div>
            <p>Loading event details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !event) {
    return (
      <div className="event-detail">
        <Breadcrumbs items={breadcrumbs} />
        <div className="container">
          <div className="event-error">
            <div className="error-content">
              <div className="error-icon">
                <i className="fas fa-exclamation-triangle"></i>
              </div>
              <h1>Event Not Found</h1>
              <p>{error || 'The requested event could not be found.'}</p>
              <div className="error-actions">
                <Link to="/events" className="btn btn-primary">
                  <i className="fas fa-arrow-left"></i>
                  Back to Events
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="event-detail">
      <Breadcrumbs items={breadcrumbs} />

      {/* Event Header */}
      <section className="event-header">
        <div className="container">
          <div className="event-header-content">
            <div className="event-image">
              {event.event_image ? (
                <img src={event.event_image} alt={event.event_name} />
              ) : (
                <div className="event-image-placeholder">
                  <i className="fas fa-baseball-ball"></i>
                </div>
              )}
            </div>

            <div className="event-info">
              <div className="event-badges">
                <span className={`badge badge-level ${event.event_level}`}>
                  <i className={`fas ${event.event_level === 'LL' ? 'fa-child' : event.event_level === 'SJ' ? 'fa-user-friends' : event.event_level === 'JL' ? 'fa-users' : 'fa-trophy'}`}></i>
                  {event.level_name}
                </span>
                <span className={`badge badge-type ${event.event_type}`}>
                  <i className={`fas ${event.event_type === 'IN' ? 'fa-globe' : 'fa-flag'}`}></i>
                  {event.type_name}
                </span>
                {event.is_featured && (
                  <span className="badge badge-featured">
                    <i className="fas fa-star"></i>
                    Featured
                  </span>
                )}
              </div>

              <h1 className="event-title">{event.event_name}</h1>

              <div className="event-meta">
                <div className="meta-item">
                  <i className="fas fa-calendar-alt"></i>
                  <div>
                    <strong>Date:</strong>
                    <span>{event.formatted_dates}</span>
                  </div>
                </div>

                <div className="meta-item">
                  <i className="fas fa-map-marker-alt"></i>
                  <div>
                    <strong>Venue:</strong>
                    <span>
                      {event.event_venue}
                      {event.event_city && `, ${event.event_city}`}
                      {event.event_state && `, ${event.event_state}`}
                    </span>
                  </div>
                </div>

                <div className="meta-item">
                  <i className="fas fa-users"></i>
                  <div>
                    <strong>Organizer:</strong>
                    <span>{event.event_organizer}</span>
                  </div>
                </div>
              </div>

              <div className="event-status-section">
                <EventStatus event={event} size="large" />
                <EventCountdown event={event} />
              </div>

              <div className="event-actions">
                <button
                  className="btn btn-secondary"
                  onClick={shareEvent}
                >
                  <i className="fas fa-share-alt"></i>
                  Share Event
                </button>
                
                {navigation.previous && (
                  <Link
                    to={`/events/${navigation.previous.slug || navigation.previous.id}`}
                    className="btn btn-outline-secondary"
                  >
                    <i className="fas fa-chevron-left"></i>
                    Previous Event
                  </Link>
                )}
                
                {navigation.next && (
                  <Link
                    to={`/events/${navigation.next.slug || navigation.next.id}`}
                    className="btn btn-outline-secondary"
                  >
                    Next Event
                    <i className="fas fa-chevron-right"></i>
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Event Content Tabs */}
      <section className="event-content">
        <div className="container">
          <div className="event-tabs">
            <div className="tab-navigation">
              <button
                className={`tab-btn ${activeTab === 'details' ? 'active' : ''}`}
                onClick={() => setActiveTab('details')}
              >
                <i className="fas fa-info-circle"></i>
                Details
              </button>
              
              <button
                className={`tab-btn ${activeTab === 'registration' ? 'active' : ''}`}
                onClick={() => setActiveTab('registration')}
              >
                <i className="fas fa-user-plus"></i>
                Registration
              </button>
              
              {event.event_rules && (
                <button
                  className={`tab-btn ${activeTab === 'rules' ? 'active' : ''}`}
                  onClick={() => setActiveTab('rules')}
                >
                  <i className="fas fa-book"></i>
                  Rules
                </button>
              )}
              
              {event.contact_info && (
                <button
                  className={`tab-btn ${activeTab === 'contact' ? 'active' : ''}`}
                  onClick={() => setActiveTab('contact')}
                >
                  <i className="fas fa-phone"></i>
                  Contact
                </button>
              )}
              
              {(event.results || event.winners) && (
                <button
                  className={`tab-btn ${activeTab === 'results' ? 'active' : ''}`}
                  onClick={() => setActiveTab('results')}
                >
                  <i className="fas fa-trophy"></i>
                  Results
                </button>
              )}
            </div>

            <div className="tab-content">
              {activeTab === 'details' && (
                <div className="tab-panel">
                  <div className="event-description">
                    {event.event_description ? (
                      <div
                        className="content-html"
                        dangerouslySetInnerHTML={{ __html: event.event_description }}
                      />
                    ) : (
                      <p>Detailed information about this event will be available soon.</p>
                    )}
                  </div>

                  {event.eligibility_criteria && (
                    <div className="eligibility-section">
                      <h3>Eligibility Criteria</h3>
                      <div
                        className="eligibility-content"
                        dangerouslySetInnerHTML={{ __html: event.eligibility_criteria }}
                      />
                    </div>
                  )}

                  {event.event_images && event.event_images.length > 0 && (
                    <div className="event-gallery">
                      <h3>Event Gallery</h3>
                      <div className="gallery-grid">
                        {event.event_images.map((image, index) => (
                          <div key={index} className="gallery-item">
                            <img src={image} alt={`${event.event_name} ${index + 1}`} />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'registration' && (
                <div className="tab-panel">
                  <RegistrationStatus event={event} />
                  
                  {event.max_teams && (
                    <TeamRegistrationProgress event={event} />
                  )}

                  <div className="registration-details">
                    <h3>Registration Information</h3>
                    
                    <div className="registration-info-grid">
                      {event.registration_start_date && (
                        <div className="info-item">
                          <strong>Registration Opens:</strong>
                          <span>{formatDate(event.registration_start_date)}</span>
                        </div>
                      )}
                      
                      {event.registration_end_date && (
                        <div className="info-item">
                          <strong>Registration Closes:</strong>
                          <span>{formatDate(event.registration_end_date)}</span>
                        </div>
                      )}
                      
                      {event.registration_fee && (
                        <div className="info-item">
                          <strong>Registration Fee:</strong>
                          <span>₹{event.registration_fee}</span>
                        </div>
                      )}
                      
                      {event.max_teams && (
                        <div className="info-item">
                          <strong>Maximum Teams:</strong>
                          <span>{event.max_teams}</span>
                        </div>
                      )}
                    </div>

                    {event.registration_status === 'open' && (
                      <div className="registration-action">
                        <p>To register for this event, please contact the organizer directly.</p>
                        <Link to="/contact" className="btn btn-primary">
                          Contact Organizer
                        </Link>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {activeTab === 'rules' && event.event_rules && (
                <div className="tab-panel">
                  <h3>Tournament Rules</h3>
                  <div
                    className="rules-content"
                    dangerouslySetInnerHTML={{ __html: event.event_rules }}
                  />
                </div>
              )}

              {activeTab === 'contact' && event.contact_info && (
                <div className="tab-panel">
                  <h3>Contact Information</h3>
                  <div
                    className="contact-content"
                    dangerouslySetInnerHTML={{ __html: event.contact_info }}
                  />
                </div>
              )}

              {activeTab === 'results' && (event.results || event.winners) && (
                <div className="tab-panel">
                  <h3>Results & Winners</h3>
                  
                  {event.winners && (
                    <div className="winners-section">
                      <h4>Winners</h4>
                      <div className="winners-list">
                        {Object.entries(event.winners).map(([position, winner]) => (
                          <div key={position} className="winner-item">
                            <div className="winner-position">{position}</div>
                            <div className="winner-name">{winner}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {event.results && (
                    <div className="results-section">
                      <h4>Detailed Results</h4>
                      <div className="results-content">
                        <pre>{JSON.stringify(event.results, null, 2)}</pre>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Similar Events */}
      {similarEvents.length > 0 && (
        <section className="similar-events">
          <div className="container">
            <h2>Similar Events</h2>
            <div className="events-grid">
              {similarEvents.map((similarEvent) => (
                <EventCard
                  key={similarEvent.id}
                  event={similarEvent}
                  viewMode="grid"
                />
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default EventDetail;